import { useCallback } from 'react'
import { useResource } from '@/components/admin/shared/hooks/use-resource'
import type { ProblemTopic } from '../../../../lib/problems/problem-topics.js'
import { trpc } from '../../../../lib/trpc.js'

export interface CompanyProblem {
  id: string
  externalId?: string | null
  title: string
  slug: string
  difficulty?: string | null
  premiumRequired?: boolean
  topics?: ProblemTopic[]
  acceptanceRate?: number | null
  freqBar?: number | null
  url?: string | null
  source?: string
}

export interface CompanyTagRef {
  id: string
  name: string
  slug: string
}

export interface CompanyParentRef {
  id: string
  name: string
  slug: string
}

export interface CompanyChildRef {
  id: string
  name: string
  slug: string
  imgUrl?: string | null
}

export interface Company {
  id: string
  name: string
  slug: string
  imgUrl?: string | null
  aliases?: string[]
  parentId?: string | null
  description?: string | null
  parent?: CompanyParentRef | null
  tags?: CompanyTagRef[]
  createdAt: Date | string
  updatedAt: Date | string
  problemCount?: number
  problems?: CompanyProblem[]
}

export interface CompanyDetail extends Company {
  problems: CompanyProblem[]
  children?: CompanyChildRef[]
}

const selectCompanies = (data: { data: Company[] } | undefined): Company[] =>
  data?.data ?? []

export function useCompanies() {
  const utils = trpc.useUtils()

  const invalidate = useCallback(() => {
    utils.admin.companies.list.invalidate()
    utils.admin.companies.getById.invalidate()
  }, [utils])

  const {
    items: companies,
    loading,
    error,
    refresh,
    create,
    update,
    remove,
  } = useResource({
    query: trpc.admin.companies.list.useQuery(),
    select: selectCompanies,
    invalidate,
    create: trpc.admin.companies.create,
    update: trpc.admin.companies.update,
    remove: trpc.admin.companies.delete,
  })

  const addCompany = async (companyData: {
    name: string
    slug?: string
    imgUrl?: string
  }): Promise<Company> => {
    return await create(companyData)
  }

  const getCompany = async (id: string): Promise<CompanyDetail> => {
    return (await utils.admin.companies.getById.fetch({
      id,
    })) as CompanyDetail
  }

  const updateCompany = async (
    id: string,
    data: {
      name?: string
      slug?: string
      imgUrl?: string | null
      aliases?: string[]
      parentId?: string | null
      description?: string | null
      tagIds?: string[]
    },
  ): Promise<Company> => {
    return await update({ id, ...data })
  }

  const deleteCompany = async (id: string): Promise<void> => {
    await remove({ id })
  }

  return {
    companies,
    loading,
    error,
    refresh,
    addCompany,
    updateCompany,
    getCompany,
    deleteCompany,
  }
}
